import { useState, useEffect, useMemo } from 'react';
import API from '../services/api';
import {
  X,
  Download,
  Calendar,
  Users,
  Euro,
  FileText,
  Search,
  Info,
  ChevronLeft,
  ChevronRight,
  Eye,
  Loader2,
  CheckCircle2
} from 'lucide-react';

function BulkInvoiceCreator({ isOpen, onClose, onSuccess }) {
  const now = new Date();

  const [selectedMonths, setSelectedMonths] = useState([]);
  const [preview, setPreview] = useState([]);
  const [loading, setLoading] = useState(false);
  const [agentSearch, setAgentSearch] = useState('');
  const [page, setPage] = useState(0);
  const [expandedRow, setExpandedRow] = useState(null);

  const monthNames = [
    'Januar', 'Februar', 'März', 'April', 'Mai', 'Juni',
    'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'
  ];

  const formatCurrency = (amount) => new Intl.NumberFormat('de-DE', {
    style: 'currency',
    currency: 'EUR'
  }).format(Number(amount || 0));

  const formatDate = (value) => {
    if (!value) return '-';
    return new Date(value).toLocaleDateString('de-DE');
  };

  const getInitials = (name = '') => {
    const parts = String(name).trim().split(/\s+/).filter(Boolean);
    if (!parts.length) return '?';
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return (parts[0][0] + parts[1][0]).toUpperCase();
  };

  const visibleMonths = useMemo(() => {
    const months = [];
    for (let y = 2025; y <= Math.max(2026, now.getFullYear()); y++) {
      for (let m = 1; m <= 12; m++) {
        const isFuture = y > now.getFullYear() || (y === now.getFullYear() && m > now.getMonth() + 1);
        months.push({ year: y, month: m, label: monthNames[m - 1], isFuture });
      }
    }
    return months;
  }, []);

  const selectedKeySet = useMemo(() => {
    return new Set(selectedMonths.map((m) => `${m.year}-${m.month}`));
  }, [selectedMonths]);

  const selectedMonthLabel = useMemo(() => {
    if (selectedMonths.length === 0) return 'Kein Monat ausgewählt';
    if (selectedMonths.length === 1) {
      const selected = selectedMonths[0];
      return `${monthNames[selected.month - 1]} ${selected.year}`;
    }
    return `${selectedMonths.length} Monate ausgewählt`;
  }, [selectedMonths]);

  const filteredPreview = useMemo(() => {
    const search = agentSearch.trim().toLowerCase();
    if (!search) return preview;
    return preview.filter((row) => String(row.agent || '').toLowerCase().includes(search));
  }, [preview, agentSearch]);

  const summary = useMemo(() => {
    const totalOrders = preview.reduce((sum, row) => sum + Number(row.orderCount || 0), 0);
    const totalAmount = preview.reduce((sum, row) => sum + Number(row.total || 0), 0);
    const agentCount = preview.length;
    const averageInvoice = agentCount > 0 ? totalAmount / agentCount : 0;

    return {
      totalOrders,
      totalAmount,
      agentCount,
      averageInvoice
    };
  }, [preview]);

  const pages = useMemo(() => {
    const itemsPerPage = 10;
    const totalPages = Math.max(1, Math.ceil(filteredPreview.length / itemsPerPage));
    const safePage = Math.min(page, totalPages - 1);
    const start = safePage * itemsPerPage;
    return {
      totalPages,
      safePage,
      itemsPerPage,
      rows: filteredPreview.slice(start, start + itemsPerPage),
      start,
      end: Math.min(start + itemsPerPage, filteredPreview.length)
    };
  }, [filteredPreview, page]);

  useEffect(() => {
    if (isOpen && selectedMonths.length > 0) {
      fetchPreview();
    } else {
      setPreview([]);
    }
  }, [selectedMonths, isOpen]);

  useEffect(() => {
    setPage(0);
  }, [agentSearch, selectedMonths]);

  const toggleMonth = (year, month) => {
    const exists = selectedMonths.find((m) => m.year === year && m.month === month);

    if (exists) {
      setSelectedMonths(selectedMonths.filter((m) => !(m.year === year && m.month === month)));
    } else {
      setSelectedMonths([...selectedMonths, { year, month }]);
    }
  };

  const selectSingleMonth = (year, month) => {
    setSelectedMonths([{ year, month }]);
  };

  const fetchPreview = async () => {
    setLoading(true);

    try {
      const result = [];

      for (const { year, month } of selectedMonths) {
        const { data } = await API.get('/invoicing/uninvoiced', {
          params: { year, month }
        });

        const orders = data.rows || [];
        const grouped = {};

        orders.forEach((order) => {
          if (!order.agent_name) return;

          if (!grouped[order.agent_name]) {
            grouped[order.agent_name] = {
              orderCount: 0,
              total: 0,
              firstDate: null,
              lastDate: null,
              orders: []
            };
          }

          const amount = parseFloat(order.betrag || 0);
          const orderDate = order.order_date ? new Date(order.order_date) : null;

          grouped[order.agent_name].orderCount += 1;
          grouped[order.agent_name].total += amount;
          grouped[order.agent_name].orders.push(order);

          if (orderDate && !Number.isNaN(orderDate.getTime())) {
            if (!grouped[order.agent_name].firstDate || orderDate < grouped[order.agent_name].firstDate) {
              grouped[order.agent_name].firstDate = orderDate;
            }
            if (!grouped[order.agent_name].lastDate || orderDate > grouped[order.agent_name].lastDate) {
              grouped[order.agent_name].lastDate = orderDate;
            }
          }
        });

        Object.entries(grouped).forEach(([agent, data]) => {
          result.push({
            year,
            month,
            agent,
            orderCount: data.orderCount,
            total: data.total,
            average: data.orderCount ? data.total / data.orderCount : 0,
            firstDate: data.firstDate,
            lastDate: data.lastDate,
            orders: data.orders
          });
        });
      }

      result.sort((a, b) => {
        if (a.year !== b.year) return a.year - b.year;
        if (a.month !== b.month) return a.month - b.month;
        return String(a.agent || '').localeCompare(String(b.agent || ''), 'de');
      });

      setPreview(result);
    } catch (err) {
      console.error('Preview Fehler:', err);
      alert('Vorschau konnte nicht geladen werden: ' + (err.response?.data?.error || err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (preview.length === 0 || loading) return;

    const ok = window.confirm(
      `Es werden ${summary.agentCount} Rechnung(en) mit ${summary.totalOrders} Position(en) über ${formatCurrency(summary.totalAmount)} erstellt.\n\nBereits fakturierte Positionen werden nicht erneut erstellt. Fortfahren?`
    );

    if (!ok) return;

    setLoading(true);

    try {
      const response = await API.post(
        '/invoicing/bulk-create',
        { months: selectedMonths },
        { responseType: 'blob' }
      );

      const blob = new Blob([response.data], { type: 'application/zip' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');

      a.href = url;
      a.download = summary.agentCount === 1 ? 'Rechnung.zip' : 'Rechnungen.zip';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);

      alert('Rechnungen erstellt');
      onSuccess?.();
      onClose?.();
    } catch (err) {
      console.error(err);
      alert('Fehler beim Erstellen: ' + (err.response?.data?.error || err.message));
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const modalStyles = {
    overlay: {
      position: 'fixed',
      inset: 0,
      background: 'rgba(15, 23, 42, 0.55)',
      backdropFilter: 'blur(6px)',
      zIndex: 1000,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: 20
    },
    modal: {
      width: 'min(1480px, 96vw)',
      maxHeight: '92vh',
      overflow: 'hidden',
      background: 'var(--bg)',
      border: '1px solid var(--border)',
      borderRadius: 18,
      boxShadow: '0 24px 80px rgba(15, 23, 42, 0.28)',
      display: 'flex',
      flexDirection: 'column'
    },
    header: {
      padding: '22px 24px',
      borderBottom: '1px solid var(--border)',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      gap: 16
    },
    body: {
      padding: 24,
      overflowY: 'auto'
    },
    card: {
      background: 'var(--bg)',
      border: '1px solid var(--border)',
      borderRadius: 16,
      boxShadow: '0 8px 24px rgba(15, 23, 42, 0.06)'
    },
    muted: {
      color: 'var(--text2)',
      fontSize: 14
    }
  };

  return (
    <div style={modalStyles.overlay}>
      <div style={modalStyles.modal}>
        <div style={modalStyles.header}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14 }}>
            <div
              style={{
                width: 42,
                height: 42,
                borderRadius: 12,
                background: 'rgba(79, 70, 229, 0.12)',
                color: 'var(--accent)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <FileText size={22} />
            </div>
            <div>
              <h2 style={{ margin: 0, fontSize: 26, lineHeight: 1.15 }}>Rechnungen erstellen</h2>
              <div style={{ ...modalStyles.muted, marginTop: 6 }}>
                Wähle einen Monat aus und prüfe vor dem Erstellen, wie viele PDFs erzeugt werden.
              </div>
            </div>
          </div>

          <button
            onClick={onClose}
            className="btn btn-ghost"
            style={{ padding: 8, borderRadius: 10 }}
            title="Schließen"
          >
            <X size={20} />
          </button>
        </div>

        <div style={modalStyles.body}>
          <section style={{ ...modalStyles.card, padding: 18, marginBottom: 18 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 14, alignItems: 'center', marginBottom: 16 }}>
              <div>
                <div style={{ fontSize: 16, fontWeight: 800 }}>1. Monat auswählen</div>
                <div style={{ ...modalStyles.muted, marginTop: 4 }}>
                  Klick wählt einen Monat. Mit erneutem Klick kann ein Monat entfernt werden.
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  className="btn btn-ghost"
                  style={{ padding: 9 }}
                  onClick={() => {
                    const lastSelected = selectedMonths[selectedMonths.length - 1];
                    if (!lastSelected) return;
                    const idx = visibleMonths.findIndex((m) => m.year === lastSelected.year && m.month === lastSelected.month);
                    const previous = visibleMonths.slice(0, idx).reverse().find((m) => !m.isFuture);
                    if (previous) selectSingleMonth(previous.year, previous.month);
                  }}
                >
                  <ChevronLeft size={18} />
                </button>
                <button
                  className="btn btn-ghost"
                  style={{ padding: 9 }}
                  onClick={() => {
                    const lastSelected = selectedMonths[selectedMonths.length - 1];
                    if (!lastSelected) return;
                    const idx = visibleMonths.findIndex((m) => m.year === lastSelected.year && m.month === lastSelected.month);
                    const next = visibleMonths.slice(idx + 1).find((m) => !m.isFuture);
                    if (next) selectSingleMonth(next.year, next.month);
                  }}
                >
                  <ChevronRight size={18} />
                </button>
              </div>
            </div>

            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(104px, 1fr))',
                gap: 10
              }}
            >
              {visibleMonths.map(({ year, month, label, isFuture }) => {
                const selected = selectedKeySet.has(`${year}-${month}`);

                return (
                  <button
                    key={`${year}-${month}`}
                    disabled={isFuture}
                    onClick={() => !isFuture && toggleMonth(year, month)}
                    style={{
                      minHeight: 66,
                      border: selected ? '1px solid transparent' : '1px solid var(--border)',
                      borderRadius: 12,
                      background: selected ? 'linear-gradient(135deg, #4f46e5, #2563eb)' : 'var(--bg)',
                      color: selected ? 'white' : isFuture ? 'var(--text3)' : 'var(--text1)',
                      opacity: isFuture ? 0.45 : 1,
                      cursor: isFuture ? 'not-allowed' : 'pointer',
                      fontWeight: selected ? 800 : 600,
                      boxShadow: selected ? '0 10px 24px rgba(37, 99, 235, 0.25)' : 'none',
                      position: 'relative'
                    }}
                  >
                    <div>{label}</div>
                    <div style={{ fontSize: 13, marginTop: 4, opacity: selected ? 0.95 : 0.75 }}>{year}</div>
                    {selected && (
                      <span
                        style={{
                          position: 'absolute',
                          left: '50%',
                          bottom: -5,
                          transform: 'translateX(-50%)',
                          width: 10,
                          height: 10,
                          borderRadius: 999,
                          background: '#22c55e',
                          border: '2px solid white'
                        }}
                      />
                    )}
                  </button>
                );
              })}
            </div>
          </section>

          <section style={{ ...modalStyles.card, padding: 18, marginBottom: 18 }}>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'minmax(260px, 1.15fr) repeat(4, minmax(170px, 1fr))',
                gap: 12,
                alignItems: 'stretch'
              }}
            >
              <div style={{ padding: 16, display: 'flex', gap: 14, alignItems: 'center' }}>
                <div
                  style={{
                    width: 56,
                    height: 56,
                    borderRadius: 16,
                    background: 'rgba(79, 70, 229, 0.12)',
                    color: 'var(--accent)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <Calendar size={28} />
                </div>
                <div>
                  <div style={{ ...modalStyles.muted, fontSize: 13 }}>Ausgewählt</div>
                  <div style={{ fontSize: 22, fontWeight: 850, marginTop: 3 }}>{selectedMonthLabel}</div>
                  <div style={{ ...modalStyles.muted, marginTop: 6 }}>Nur erstattete Positionen ohne Rechnung</div>
                </div>
              </div>

              <MetricCard icon={<FileText size={20} />} label="Fakturierbare Positionen" value={summary.totalOrders} sub="Erstattete Bestellungen" color="#2563eb" />
              <MetricCard icon={<Users size={20} />} label="Anzahl Agenten" value={summary.agentCount} sub="Rechnungen / Dateien" color="#16a34a" />
              <MetricCard icon={<Euro size={20} />} label="Gesamtbetrag" value={formatCurrency(summary.totalAmount)} sub="Zu fakturierender Betrag" color="#7c3aed" />
              <MetricCard icon={<Euro size={20} />} label="Ø Betrag pro Rechnung" value={formatCurrency(summary.averageInvoice)} sub="Durchschnitt pro Agent" color="#d97706" />
            </div>

            <div
              style={{
                marginTop: 14,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: 12,
                padding: '12px 14px',
                borderRadius: 12,
                background: 'rgba(37, 99, 235, 0.08)',
                border: '1px solid rgba(37, 99, 235, 0.18)',
                color: '#1d4ed8'
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 14 }}>
                <Info size={18} />
                <span>Es wird pro Agent genau eine Rechnung/PDF für alle offenen erstatteten Positionen erstellt.</span>
              </div>
              <button
                onClick={handleCreate}
                disabled={loading || preview.length === 0}
                className="btn"
                style={{
                  background: preview.length === 0 ? 'var(--bg2)' : 'var(--accent)',
                  color: preview.length === 0 ? 'var(--text3)' : 'white',
                  border: 'none',
                  padding: '10px 16px',
                  fontWeight: 800,
                  whiteSpace: 'nowrap'
                }}
              >
                {loading ? <Loader2 size={16} style={{ marginRight: 8 }} /> : <Download size={16} style={{ marginRight: 8 }} />}
                Rechnungen erstellen ({summary.agentCount} PDFs)
              </button>
            </div>
          </section>

          <section style={{ ...modalStyles.card, overflow: 'hidden' }}>
            <div
              style={{
                padding: 18,
                borderBottom: '1px solid var(--border)',
                display: 'flex',
                justifyContent: 'space-between',
                gap: 12,
                alignItems: 'center',
                flexWrap: 'wrap'
              }}
            >
              <div>
                <div style={{ fontSize: 16, fontWeight: 800 }}>2. Vorschau – nach Agenten</div>
                <div style={{ ...modalStyles.muted, marginTop: 4 }}>
                  {filteredPreview.length === preview.length
                    ? `${preview.length} Rechnung(en) in der Vorschau`
                    : `${filteredPreview.length} von ${preview.length} Rechnung(en) sichtbar`}
                </div>
              </div>

              <div style={{ position: 'relative', minWidth: 260 }}>
                <Search size={17} style={{ position: 'absolute', left: 12, top: 11, color: 'var(--text3)' }} />
                <input
                  value={agentSearch}
                  onChange={(e) => setAgentSearch(e.target.value)}
                  placeholder="Agent suchen..."
                  style={{
                    width: '100%',
                    padding: '10px 12px 10px 38px',
                    border: '1px solid var(--border)',
                    borderRadius: 10,
                    background: 'var(--bg)',
                    color: 'var(--text1)'
                  }}
                />
              </div>
            </div>

            {loading ? (
              <div style={{ padding: 46, textAlign: 'center', color: 'var(--text2)' }}>
                <Loader2 size={24} style={{ marginBottom: 10 }} />
                <div>Vorschau wird geladen…</div>
              </div>
            ) : selectedMonths.length === 0 ? (
              <EmptyState title="Noch kein Monat ausgewählt" text="Wähle oben einen Monat aus, um eine Vorschau zu sehen." />
            ) : preview.length === 0 ? (
              <EmptyState title="Keine Rechnungen möglich" text="Für die Auswahl gibt es keine erstatteten Positionen ohne Rechnung." />
            ) : (
              <>
                <div style={{ overflowX: 'auto' }}>
                  <table className="table" style={{ minWidth: 980 }}>
                    <thead>
                      <tr>
                        <th style={{ width: 50 }}>#</th>
                        <th>Agent</th>
                        <th>Monat</th>
                        <th style={{ textAlign: 'right' }}>Bestellungen</th>
                        <th style={{ textAlign: 'right' }}>Betrag</th>
                        <th>Von</th>
                        <th>Bis</th>
                        <th style={{ textAlign: 'right' }}>Ø Betrag</th>
                        <th style={{ textAlign: 'right' }}>Aktion</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pages.rows.map((row, index) => {
                        const globalIndex = pages.start + index + 1;
                        const key = `${row.year}-${row.month}-${row.agent}`;
                        const expanded = expandedRow === key;

                        return (
                          <>
                            <tr key={key}>
                              <td>{globalIndex}</td>
                              <td>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                  <div
                                    style={{
                                      width: 32,
                                      height: 32,
                                      borderRadius: 999,
                                      background: 'rgba(79, 70, 229, 0.12)',
                                      color: 'var(--accent)',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      fontWeight: 800,
                                      fontSize: 12
                                    }}
                                  >
                                    {getInitials(row.agent)}
                                  </div>
                                  <strong>{row.agent}</strong>
                                </div>
                              </td>
                              <td>{monthNames[row.month - 1]} {row.year}</td>
                              <td style={{ textAlign: 'right' }}>{row.orderCount}</td>
                              <td style={{ textAlign: 'right', fontWeight: 800 }}>{formatCurrency(row.total)}</td>
                              <td>{formatDate(row.firstDate)}</td>
                              <td>{formatDate(row.lastDate)}</td>
                              <td style={{ textAlign: 'right' }}>{formatCurrency(row.average)}</td>
                              <td style={{ textAlign: 'right' }}>
                                <button
                                  className="btn btn-ghost"
                                  style={{ padding: '7px 10px', fontSize: 12 }}
                                  onClick={() => setExpandedRow(expanded ? null : key)}
                                >
                                  <Eye size={14} style={{ marginRight: 6 }} />
                                  Details
                                </button>
                              </td>
                            </tr>
                            {expanded && (
                              <tr>
                                <td colSpan={9} style={{ background: 'var(--bg2)', padding: 0 }}>
                                  <div style={{ padding: 14 }}>
                                    <div style={{ fontWeight: 800, marginBottom: 10 }}>
                                      Positionen für {row.agent}
                                    </div>
                                    <div style={{ overflowX: 'auto' }}>
                                      <table className="table" style={{ fontSize: 12 }}>
                                        <thead>
                                          <tr>
                                            <th>Datum</th>
                                            <th>Bestellung</th>
                                            <th>Account</th>
                                            <th>Produkt</th>
                                            <th style={{ textAlign: 'right' }}>Betrag</th>
                                            <th>Payment</th>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {(row.orders || []).map((order) => (
                                            <tr key={order.id}>
                                              <td>{formatDate(order.order_date)}</td>
                                              <td style={{ fontFamily: 'monospace' }}>{order.order_number}</td>
                                              <td>{order.account_name || '-'}</td>
                                              <td title={order.product_name} style={{ maxWidth: 420, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                {order.product_name || '-'}
                                              </td>
                                              <td style={{ textAlign: 'right', fontWeight: 700 }}>{formatCurrency(order.betrag)}</td>
                                              <td style={{ fontFamily: 'monospace' }}>{order.payment_id || '-'}</td>
                                            </tr>
                                          ))}
                                        </tbody>
                                      </table>
                                    </div>
                                  </div>
                                </td>
                              </tr>
                            )}
                          </>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                <div
                  style={{
                    padding: 14,
                    borderTop: '1px solid var(--border)',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: 12,
                    color: 'var(--text2)',
                    fontSize: 13
                  }}
                >
                  <div>
                    Zeige {filteredPreview.length === 0 ? 0 : pages.start + 1} bis {pages.end} von {filteredPreview.length} Einträgen
                  </div>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <button
                      className="btn btn-ghost"
                      style={{ padding: 8 }}
                      disabled={pages.safePage === 0}
                      onClick={() => setPage((p) => Math.max(0, p - 1))}
                    >
                      <ChevronLeft size={16} />
                    </button>
                    <div style={{ fontWeight: 800, color: 'var(--text1)' }}>{pages.safePage + 1} / {pages.totalPages}</div>
                    <button
                      className="btn btn-ghost"
                      style={{ padding: 8 }}
                      disabled={pages.safePage >= pages.totalPages - 1}
                      onClick={() => setPage((p) => Math.min(pages.totalPages - 1, p + 1))}
                    >
                      <ChevronRight size={16} />
                    </button>
                  </div>
                </div>
              </>
            )}
          </section>
        </div>
      </div>
    </div>
  );
}

function MetricCard({ icon, label, value, sub, color }) {
  return (
    <div
      style={{
        padding: 16,
        border: '1px solid var(--border)',
        borderRadius: 14,
        background: 'var(--bg)',
        minHeight: 104
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
        <div
          style={{
            width: 34,
            height: 34,
            borderRadius: 999,
            background: `${color}22`,
            color,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          {icon}
        </div>
        <div style={{ fontSize: 13, color: 'var(--text2)', fontWeight: 700 }}>{label}</div>
      </div>
      <div style={{ fontSize: 28, fontWeight: 900, color }}>{value}</div>
      <div style={{ fontSize: 13, color: 'var(--text2)', marginTop: 6 }}>{sub}</div>
    </div>
  );
}

function EmptyState({ title, text }) {
  return (
    <div style={{ padding: 48, textAlign: 'center', color: 'var(--text2)' }}>
      <CheckCircle2 size={30} style={{ marginBottom: 12, opacity: 0.55 }} />
      <div style={{ fontWeight: 800, color: 'var(--text1)', marginBottom: 6 }}>{title}</div>
      <div>{text}</div>
    </div>
  );
}

export default BulkInvoiceCreator;
